/**
 * PCLab2 下载页的目标平台（target）与 mode 键规则。
 *
 * 与 pclab-train/scripts/lib/target.mjs、api-main 的
 * PCLAB2_TARGETED_MODE_PATTERN、management-api 的
 * CLIENT_RELEASE_PRODUCTS.pclab2 保持一致：新记录一律
 * `{role}2-{target}`；无后缀旧值（`teacher2` 等）归入 win-x86
 * 语义，只读兜底、聚合进 32 位分节。
 */
export type PclabTarget = 'win-x86'; // | 'win-x64' 暂停
export type PclabSetupRole = 'teacher' | 'student';

/** 当前仅展示 x86；原 x64 分节暂时停用。 */
export const PCLAB_TARGETS: readonly PclabTarget[] = [/* 'win-x64', 暂停 */ 'win-x86'];

export const PCLAB_TARGET_LABELS: Readonly<Record<PclabTarget, string>> = {
  // 'win-x64': 'Windows 64 位',
  'win-x86': 'Windows 32 位',
};

export const PCLAB_SETUP_ROLES: readonly PclabSetupRole[] = ['teacher', 'student'];

/**
 * 学生端 Linux 信创包：每架构一个 portable（.tar.gz）与一个 deb mode，
 * 与 api-main `PCLAB2_STUDENT_LINUX_MODE_PATTERN`、management-api
 * `CLIENT_RELEASE_PRODUCTS.pclab2` 保持一致。教师端与更新文件暂不开放 Linux。
 */
export interface StudentLinuxPackage {
  readonly archLabel: string;
  readonly archDetail: string;
  readonly portableMode: string;
  readonly debMode: string;
}

export const PCLAB_STUDENT_LINUX_PACKAGES: readonly StudentLinuxPackage[] = [
  {
    archLabel: 'x86_64',
    archDetail: '统信 UOS / 银河麒麟等常见 64 位机型',
    portableMode: 'student2-linux-x86_64-portable',
    debMode: 'student2-linux-x86_64-deb',
  },
  {
    archLabel: 'ARM64',
    archDetail: '麒麟 ARM64、飞腾/鲲鹏机型',
    portableMode: 'student2-linux-arm64-portable',
    debMode: 'student2-linux-arm64-deb',
  },
  {
    archLabel: 'LoongArch',
    archDetail: '龙芯 3A5000/3A6000、Loongnix/UOS 龙芯版',
    portableMode: 'student2-linux-loongarch64-portable',
    debMode: 'student2-linux-loongarch64-deb',
  },
];

/** 新发布的完整 mode：`{role}2-{target}`，如 `teacher2-win-x64`。 */
export function getModeKey(role: PclabSetupRole, target: PclabTarget): string {
  return `${role}2-${target}`;
}

/** 无后缀旧 mode：等价 win-x86 的历史数据，只读兜底。 */
export function getUnsuffixedModeKey(role: PclabSetupRole): string {
  return `${role}2`;
}

/**
 * 分节聚合的 mode 顺序：新键在前；win-x86 节追加 无后缀键兜底。
 * 下载与上报统一使用记录自身命中的完整 mode（含无后缀原值）。
 */
export function getSectionModes(role: PclabSetupRole, target: PclabTarget): readonly string[] {
  return target === 'win-x86'
    ? [getModeKey(role, target), getUnsuffixedModeKey(role)]
    : [getModeKey(role, target)];
}

/**
 * setup 安装包版本按点分数字（semver 形）比较，供分节历史版本合并排序。
 * `updates` 机房目录增量是纯数字版本序列、不属于安装包历史，不经此函数展示。
 */
export function compareSetupVersions(
  left: string | undefined,
  right: string | undefined,
): number {
  const partsLeft = (left ?? '').split('.');
  const partsRight = (right ?? '').split('.');
  const length = Math.max(partsLeft.length, partsRight.length);
  for (let index = 0; index < length; index += 1) {
    const partLeft = partsLeft[index] ?? '';
    const partRight = partsRight[index] ?? '';
    if (partLeft === partRight) continue;
    const numLeft = Number(partLeft);
    const numRight = Number(partRight);
    if (partLeft !== '' && partRight !== '' && Number.isInteger(numLeft) && Number.isInteger(numRight)) {
      return numLeft - numRight;
    }
    return partLeft < partRight ? -1 : 1;
  }
  return 0;
}

interface NavigatorUAHighEntropy {
  readonly architecture?: string;
  readonly bitness?: string;
  readonly platform?: string;
}

interface NavigatorUADataLike {
  getHighEntropyValues(hints: readonly string[]): Promise<NavigatorUAHighEntropy>;
}

/** 可注入的 navigator 子集，便于测试与 SSR 兜底。 */
export interface NavigatorLike {
  readonly userAgent?: string;
  readonly platform?: string;
  readonly userAgentData?: NavigatorUADataLike;
}

// const WINDOWS_X64_PATTERN = /Win64|x64|WOW64|ARM64/iu;
const WINDOWS_PATTERN = /Windows|Win32/iu;

/** UA/platform 串同步检测：WOW64（32 位浏览器跑 64 位系统）判 win-x64。 */
export function detectWindowsTargetSync(nav: NavigatorLike): PclabTarget | undefined {
  const ua = nav.userAgent ?? '';
  const platform = nav.platform ?? '';
  // x64 客户端暂停：64 位 Windows 也使用 x86 客户端。
  // if (WINDOWS_X64_PATTERN.test(ua) || WINDOWS_X64_PATTERN.test(platform)) return 'win-x64';
  if (WINDOWS_PATTERN.test(ua) || /^Win/iu.test(platform)) return 'win-x86';
  return undefined;
}

/**
 * 浏览器 OS 架构探测：优先 `userAgentData` 高熵值（Chromium），失败或缺失
 * 时回退 UA/platform 串。返回 `undefined` 表示非 Windows 或无法判断——
 * 调用方不得默认 64 位；浏览器 JS 无法可靠判断 Windows 内核具体版本，
 * 页面文案也不做此承诺。
 */
export async function detectWindowsTarget(nav: NavigatorLike): Promise<PclabTarget | undefined> {
  if (nav.userAgentData) {
    try {
      const values = await nav.userAgentData.getHighEntropyValues([
        'architecture',
        'bitness',
        'platform',
      ]);
      if (values.platform === 'Windows') {
        // if (values.bitness === '64') return 'win-x64';
        return 'win-x86';
        // if (values.bitness === '32' || values.architecture === 'x86') return 'win-x86';
        // platform 明确为 Windows 但位数未知：继续走 UA 串兜底。
      } else if (typeof values.platform === 'string' && values.platform !== '') {
        return undefined;
      }
    } catch {
      // 高熵值被拒绝时回退 UA/platform 串。
    }
  }
  return detectWindowsTargetSync(nav);
}
